// "Show where I am": a button over the zoom buttons. Nothing is asked of the
// browser until it's tapped. Then the map jumps to the viewer once, at the
// zoom Follow uses, and a dot marks them while the page is open. The position
// stays in the page: no request or log carries it.

import { Marker } from 'maplibre-gl';
import type { Map as MapLibreMap } from 'maplibre-gl';
import type { LatLon } from '@tol/core/types';
import { ZOOM_OFFSET } from '@tol/core/view';

export const LOCATE_TEXT = {
  button: 'Show where I am',
  here: 'You are here',
  denied: "Location access is off, so the map can't show where you are.",
  unavailable: "Couldn't find where you are.",
  outside: "You're outside this map.",
} as const;

// GeolocationPositionError.PERMISSION_DENIED.
const PERMISSION_DENIED = 1;

// What the page reads of the browser's geolocation (navigator.geolocation).
export interface Geolocator {
  readonly watchPosition: (
    fix: (p: { readonly coords: { readonly latitude: number; readonly longitude: number } }) => void,
    fail: (e: { readonly code: number }) => void,
    options: Readonly<PositionOptions>,
  ) => number;
  readonly clearWatch: (id: number) => void;
}

export interface LocateHooks {
  // null: the browser has none.
  readonly geolocation: Geolocator | null;
  readonly inBounds: (at: LatLon) => boolean;
  readonly center: (at: LatLon) => void;
  // Where the dot goes; null: no dot.
  readonly mark: (at: LatLon | null) => void;
  readonly say: (text: string) => void;
}

// The button's tap: the first starts watching the position and centers on
// the first fix; later ones center on the latest. An error is said only to a
// tap still waiting for a fix. A position briefly lost keeps the watch and
// the dot where it was; denied permission stops watching and removes it.
export function locator(hooks: LocateHooks): () => void {
  let watch: number | null = null;
  let last: LatLon | null = null;
  let waiting = false;

  function arrive(at: LatLon): void {
    if (hooks.inBounds(at)) hooks.center(at);
    else hooks.say(LOCATE_TEXT.outside);
  }

  return () => {
    const { geolocation } = hooks;
    if (!geolocation) {
      hooks.say(LOCATE_TEXT.unavailable);
      return;
    }
    if (last) {
      arrive(last);
      return;
    }
    waiting = true;
    if (watch !== null) return;
    watch = geolocation.watchPosition(
      (p) => {
        last = { lat: p.coords.latitude, lon: p.coords.longitude };
        hooks.mark(last);
        if (!waiting) return;
        waiting = false;
        arrive(last);
      },
      (e) => {
        const denied = e.code === PERMISSION_DENIED;
        if (waiting) hooks.say(denied ? LOCATE_TEXT.denied : LOCATE_TEXT.unavailable);
        waiting = false;
        if (!denied) return;
        if (watch !== null) geolocation.clearWatch(watch);
        watch = null;
        last = null;
        hooks.mark(null);
      },
      { enableHighAccuracy: true },
    );
  };
}

// What the move to the viewer uses of the map (MapLibre's Map).
export interface CenterMap {
  readonly getZoom: () => number;
  readonly jumpTo: (options: {
    readonly center: { readonly lng: number; readonly lat: number };
    readonly zoom: number;
  }) => unknown;
}

// The camera's move to the viewer: straight there, no animation, at least as
// close as Follow's zoom.
export function centerOn(map: CenterMap, at: LatLon): void {
  map.jumpTo({ center: { lng: at.lon, lat: at.lat }, zoom: Math.max(map.getZoom(), 14 - ZOOM_OFFSET) });
}

export interface LocateOptions {
  readonly say: (text: string) => void;
  // Before the camera moves to the viewer (Follow stops).
  readonly beforeCenter: () => void;
}

export function addLocateControl(map: MapLibreMap, options: LocateOptions): void {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'locate-button';
  button.title = LOCATE_TEXT.button;
  button.setAttribute('aria-label', LOCATE_TEXT.button);
  button.innerHTML =
    '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">' +
    '<circle cx="10" cy="10" r="5.5"/><circle cx="10" cy="10" r="1.6" fill="currentColor" stroke="none"/>' +
    '<path d="M10 1.5v3M10 15.5v3M1.5 10h3M15.5 10h3"/></svg>';

  const dot = document.createElement('div');
  dot.className = 'locate-dot';
  dot.title = LOCATE_TEXT.here;
  dot.setAttribute('role', 'img');
  dot.setAttribute('aria-label', LOCATE_TEXT.here);
  const marker = new Marker({ element: dot });
  let shown = false;

  button.addEventListener(
    'click',
    locator({
      geolocation: 'geolocation' in navigator ? navigator.geolocation : null,
      inBounds: (at) => map.getMaxBounds()?.contains([at.lon, at.lat]) ?? true,
      center: (at) => {
        options.beforeCenter();
        centerOn(map, at);
      },
      mark: (at) => {
        button.classList.toggle('on', at !== null);
        if (at === null) {
          marker.remove();
          shown = false;
          return;
        }
        marker.setLngLat([at.lon, at.lat]);
        if (!shown) marker.addTo(map);
        shown = true;
      },
      say: options.say,
    }),
  );

  const group = document.createElement('div');
  group.className = 'maplibregl-ctrl maplibregl-ctrl-group locate';
  group.append(button);
  map.addControl(
    {
      onAdd: () => group,
      onRemove: () => {
        group.remove();
      },
    },
    'bottom-right',
  );
}
